import { BlockStack, Box, Link, Text } from '@shopify/polaris';
import { GI18NText } from '~/presentation/common';
import { useTranslation } from 'react-i18next';
import { PreferenceType } from '../types';

const COMPONENT_PREFERENCES_PROPS: Record<PreferenceType, { title: string; subtitle?: string }> = {
  [PreferenceType.PLAN_INFORMATION]: {
    title: 'Plan information',
    subtitle:
      'Manage or change your subscription. View our [1]{{termOrService}}[] and [2]{{privacyPolicy}}[].',
  },
  [PreferenceType.USAGE_BILLING]: {
    title: 'Usage billing',
    subtitle: 'Manage your billing & payment status',
  },
  [PreferenceType.BILLING_SUMMARY]: {
    title: 'Billing summary',
    subtitle: 'Manage your billing & payment status',
  },
  [PreferenceType.PROFILE_SETTINGS]: {
    title: 'Account settings',
    subtitle: 'Manage your account and staff permission (soon).',
  },
  [PreferenceType.STORE_SETTINGS]: {
    title: 'Store settings',
    subtitle: 'Manage your experience with GemX.',
  },
  [PreferenceType.CUSTOM_CODE]: {
    title: 'Add code to theme',
    subtitle: 'Custom your store, add tracking code and more.',
  },
  [PreferenceType.TEMPLATES_BY_GEMX]: {
    title: 'Templates created by GemX',
    subtitle: 'Manage templates created based on Control by GemX.',
  },
  [PreferenceType.LANGUAGE_SETTINGS]: {
    title: 'Language settings',
    // subtitle: 'Manage your experience with GemX.',
  },
  [PreferenceType.DANGER_ZONE]: {
    title: 'Danger zone',
  },
};

export interface SettingWrapperProps {
  children?: React.ReactNode;
  type: PreferenceType;
  width?: string;
}

export const SettingWrapper: React.FC<SettingWrapperProps> = ({ children, type, width }) => {
  const { t } = useTranslation();

  const { title, subtitle } = COMPONENT_PREFERENCES_PROPS[type];

  const renderSubtitle = () => {
    if (type !== PreferenceType.PLAN_INFORMATION)
      return (
        <Text as="span" tone="subdued">
          {subtitle}
        </Text>
      );

    return (
      <GI18NText
        as="span"
        tone="subdued"
        transformers={{
          1: (text) => (
            <Link monochrome url="https://gemexp.net/pages/term-of-service" target="_blank">
              {text}
            </Link>
          ),
          2: (text) => (
            <Link monochrome url="https://gemexp.net/pages/privacy-policy" target="_blank">
              {text}
            </Link>
          ),
        }}
      >
        {t(subtitle!, { termOrService: 'terms of service', privacyPolicy: 'privacy policy' })}
      </GI18NText>
    );
  };

  return (
    <Box paddingInline={{ xs: '200', sm: '0' }} width={width}>
      <BlockStack gap="400">
        <BlockStack gap="100">
          <Text as="p" fontWeight="semibold" variant="headingMd">
            {t(title)}
          </Text>

          {subtitle && (
            <Text as="span" tone="subdued" variant="headingXs" fontWeight="regular">
              {renderSubtitle()}
            </Text>
          )}
        </BlockStack>

        {children}
      </BlockStack>
    </Box>
  );
};
